import { Select } from 'antd';
import type { DefaultOptionType, SelectProps } from 'antd/es/select';
import { useEffect, useId, useMemo, useState } from 'react';
import cn from 'classnames';
import styles from './select.module.scss';
import { SizeType } from 'antd/es/config-provider/SizeContext';

export type AppSelectProps<
    V = any,
    O extends DefaultOptionType = DefaultOptionType
> = SelectProps<V, O> & {
    label?: string;
    requiredMark?: boolean;
    footnoteMark?: boolean;
    size?: SizeType;
    underline?: boolean;
};

export const AppSelect = ({
    label,
    value,
    defaultValue,
    onFocus,
    onBlur,
    onDropdownVisibleChange,
    requiredMark,
    footnoteMark,
    className,
    ...rest
}: AppSelectProps) => {
    const id = useId();
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);

    const hasValue = useMemo(() => {
        const v = value ?? defaultValue;
        if (Array.isArray(v)) return v.length > 0;
        if (v && typeof v === 'object' && 'value' in (v as any)) return true;
        return v !== undefined && String(v ?? '').trim().length > 0;
    }, [value, defaultValue]);

    const isOccupied = focused || open || hasValue;

    useEffect(() => {
        if (!open) return;
    }, [open]);

    return (
        <div
            className={cn(styles.root, className, {
                [styles.isFocused]: focused || open,
                [styles.hasValue]: hasValue,
            })}
        >
            <Select
                id={rest.id ?? id}
                {...rest}
                value={value}
                defaultValue={defaultValue}
                bordered={false}
                rootClassName={cn(styles.control, (rest as any).rootClassName)}
                dropdownClassName="app-select-dropdown"
                suffixIcon={
                    <img
                        src="/assets/icons/Polygon.svg"
                        alt=""
                        className={cn(styles.icon, { [styles.open]: open })}
                    />
                }
                onFocus={e => {
                    setFocused(true);
                    onFocus?.(e);
                }}
                onBlur={e => {
                    setFocused(false);
                    onBlur?.(e);
                }}
                onDropdownVisibleChange={v => {
                    setOpen(v);
                    onDropdownVisibleChange?.(v);
                }}
            />

            {label && (
                <label
                    htmlFor={rest.id ?? id}
                    className={cn(
                        styles.label,
                        isOccupied ? styles.asLabel : styles.asPlaceholder
                    )}
                >
                    {label}
                    {requiredMark && (
                        <span className={styles.requiredMark}>*</span>
                    )}
                    {footnoteMark && (
                        <span className={styles.footnoteMark}>*</span>
                    )}
                </label>
            )}
        </div>
    );
};
